import { inject, injectable } from 'inversify'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import type { AccessControlStrategy } from '@/services/accessControl/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type GettingStartedInfoService from '@/services/domain/gettingStartedInfoService'
import type { FeatureFlagsService } from '@/services/featureFlagsService'
import type UserService from '@/services/domain/user/userService'
import type TeamWorkflowService from '@/services/domain/accounts/teamWorkflowService'
import { UserRole } from '@/services/domain/user/types'
import { FeatureFlags } from '@/services/types'
import type UserSettingsService from '@/services/domain/user/userSettingsService'
import type { WebWidgetsAccessControlService } from '@/services/domain/messages/settings/webWidgets/webWidgetsAccessControlService'
import type PermissionsService from '@/services/permissionsService'
import type { TmRoutes } from '@/services/route/routerTypes'
import type { DealsAccessControlService } from '@/services/domain/deals/dealsAccessControlService'
import type { TasksAccessControlService } from '@/services/domain/tasks/tasksAccessControlService'
import type ByocService from '@/services/domain/byoc/byocService'
import type RouterService from '@/services/route/routerService'
import type AccountSettingsSecurityService from '@/services/domain/accounts/accountSettingsSecurityService'
import { composeMessageRouteName } from '@/routes/user/compose'
import type { ComposeMessageRouteQueryParams } from '@/components/views/compose/types'
import type BillingService from '@/services/domain/billing/billingService'
import TmAbsurdError from '@/core/error/tmAbsurdError'
import { UserSettingsRoutes } from '@/routes/user/settings'
import TmLogicError from '@/core/error/tmLogicError'
import { AutomationRoutes } from '@/routes/user/automations/types'
import { contactsListsFoldersContactListRouteName, contactsListsFoldersRouteName } from '@/routes/user/contacts/types'
import { ListIdRouteParam } from '@/components/views/contacts/types'
import type EmailCampaignDomainService from '@/services/domain/campaign/emailCampaignDomainService'
import type EmailCampaignDomainsPreloaderService from '@/services/preloaders/emailCampaignDomainsPreloaderService'

@injectable()
export class GettingStartedStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.GettingStartedInfoService)
    protected readonly gettingStartedInfoService: GettingStartedInfoService,
  ) {}

  public async resolve() {
    await this.gettingStartedInfoService.findOrFillInfo()
  }

  public isAccessAllowed(): boolean {
    const info = this.gettingStartedInfoService.findInfoOrFail()
    return info.showGettingStartedPage
  }
}

@injectable()
export class TenDlcStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.UserSettingsService) protected readonly userSettingsService: UserSettingsService) {}

  public isAccessAllowed(): boolean {
    return this.userSettingsService.currentUserSettingsOrNull()?.showTenDlcTab ?? false
  }
}

@injectable()
export class TollFreeStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.UserSettingsService) protected readonly userSettingsService: UserSettingsService) {}

  public isAccessAllowed(): boolean {
    return this.userSettingsService.currentUserSettingsOrNull()?.showTollFreeTab ?? false
  }
}

@injectable()
export class SenderIdsStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.UserSettingsService) protected readonly userSettingsService: UserSettingsService) {}

  public isAccessAllowed(): boolean {
    return this.userSettingsService.currentUserSettingsOrNull()?.showSenderIds ?? false
  }
}

@injectable()
export class SubAccountsImportFlowStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.UserService) protected readonly userService: UserService) {}

  public isAccessAllowed(): boolean {
    return this.userService.isAllowInviteSubAccount()
  }
}

@injectable()
export class UserWorkflowStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService) {}

  public isAccessAllowed(): boolean {
    return this.teamWorkflowService.isUsersWorkflow()
  }
}

@injectable()
export class SubAccountsWorkflowStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService) {}

  public isAccessAllowed(): boolean {
    return this.teamWorkflowService.isSubaccountsWorkflow()
  }
}

@injectable()
export class SubAccountDetailsStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService) {}

  public isAccessAllowed(): boolean {
    return !this.teamWorkflowService.isUsersWorkflow()
  }
}

@injectable()
export class UsersWorkflowNotUserRoleStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {}

  public isAccessAllowed(): boolean {
    return this.teamWorkflowService.isUsersWorkflowNotUserRole()
  }
}

@injectable()
export class NotUserRoleStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.UserService) protected readonly userService: UserService) {}

  public isAccessAllowed(): boolean {
    return this.userService.currentUser().subaccountType !== UserRole.USER
  }
}

@injectable()
export class UsersWorkflowSuperAdminRoleStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {}

  public isAccessAllowed(): boolean {
    if (!this.teamWorkflowService.isUsersWorkflow()) {
      return true
    }
    return this.userService.currentUser().subaccountType === UserRole.SUPER_ADMIN
  }
}

@injectable()
export class TasksReadOnlyStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.TasksAccessControlService)
    protected readonly tasksAccessControlService: TasksAccessControlService,
  ) {}

  public isAccessAllowed(): boolean {
    return this.tasksAccessControlService.isAccessAllowed()
  }
}

@injectable()
export class DealsReadOnlyStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.DealsAccessControlService) protected dealsAccessControlService: DealsAccessControlService,
  ) {}

  public isAccessAllowed(): boolean {
    return this.dealsAccessControlService.isAccessAllowed()
  }
}

@injectable()
export class SuperAdminRoleStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.UserService) protected readonly userService: UserService) {}

  public isAccessAllowed(): boolean {
    return this.userService.currentUser().subaccountType === UserRole.SUPER_ADMIN
  }
}

@injectable()
export class UsersWorkflowAdminRoleStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {}

  public isAccessAllowed(): boolean {
    const role = this.userService.currentUser().subaccountType
    if (this.teamWorkflowService.isUsersWorkflow()) {
      return role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN
    }
    return role === UserRole.SUPER_ADMIN
  }
}

@injectable()
export class WebWidgetsStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.WebWidgetsAccessControlService)
    protected readonly webWidgetsAccessControlService: WebWidgetsAccessControlService,
  ) {}

  public isAccessAllowed(): boolean {
    return this.webWidgetsAccessControlService.isAllowWebWidgetForCurrentUser()
  }
}

@injectable()
export class MonthlyPlansStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.PermissionsService) private readonly permissionsService: PermissionsService) {}

  public async resolve() {
    await this.permissionsService.getPermissionsIfNotLoaded()
  }

  public isAccessAllowed() {
    return this.permissionsService.isActivePermission('autorechargeSettings')
  }

  public getRedirectRoute(): TmRoutes {
    return {
      name: 'payment',
    }
  }
}

@injectable()
export class ByocStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.ByocService) protected byocService: ByocService,
    @inject(SERVICE_TYPES.PermissionsService) private readonly permissionsService: PermissionsService,
  ) {}

  public async resolve() {
    const promisesList: Promise<unknown>[] = [this.permissionsService.getPermissionsIfNotLoaded()]
    const hasSettings = this.byocService.hasSettings()
    if (!hasSettings) {
      promisesList.push(this.byocService.fetchSettings())
    }
    await Promise.all(promisesList)
  }

  public isAccessAllowed() {
    return this.byocService.isAvailable()
  }
}

@injectable()
export class PaymentCongratsStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.RouterService) protected routerService: RouterService) {}

  public isAccessAllowed() {
    const allowedRoutes: TmRoutes['name'][] = ['payment.congratsRedirect']

    const previousRoute = this.routerService.getPreviousRoute()
    // page should not be available by the direct link
    return !!allowedRoutes.find((t) => previousRoute?.name === t)
  }
}

@injectable()
export class TwoFactorSetupStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.AccountSettingsSecurityService)
    protected accountSettingsSecurityService: AccountSettingsSecurityService,
  ) {}

  public async resolve() {
    if (this.accountSettingsSecurityService.getLoadedSettings()) {
      return
    }
    await this.accountSettingsSecurityService.getSettings()
  }

  public isAccessAllowed() {
    return !this.accountSettingsSecurityService.getLoadedSettingsOrFail().hasPhoneOrTotpTwoFa
  }

  public getRedirectRoute(): TmRoutes {
    return {
      name: 'user.accounts.security',
    }
  }
}

@injectable()
export class PaymentStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.PermissionsService) private readonly permissionsService: PermissionsService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {}

  public async resolve() {
    await this.permissionsService.getPermissionsIfNotLoaded()
  }

  public isAccessAllowed() {
    return !this.permissionsService.isActivePermission('autorechargeSettings')
  }

  public getRedirectRoute() {
    return {
      name: composeMessageRouteName,
      query: {
        openCreditModal: 'true',
      } satisfies ComposeMessageRouteQueryParams,
    }
  }
}

@injectable()
export class PointAiManagementStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.UsersWorkflowNotUserRoleStrategy)
    private readonly usersWorkflowNotUserRoleStrategy: UsersWorkflowNotUserRoleStrategy,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {}

  public isAccessAllowed() {
    return this.usersWorkflowNotUserRoleStrategy.isAccessAllowed()
  }
}

@injectable()
export class PrivacyPolicyStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService) {}

  public isAccessAllowed(): boolean {
    return this.teamWorkflowService.mustAcceptTermsUpdate()
  }

  public getRedirectRoute(): TmRoutes {
    return { name: composeMessageRouteName }
  }
}

@injectable()
export class BillingStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.BillingService) protected readonly billingService: BillingService) {}

  public isAccessAllowed(): boolean {
    return this.billingService.isAllowAccessForBilling()
  }
}

type SettingsWorkspaceAccessRejectionReason = 'role' | 'flow'

@injectable()
abstract class SettingsWorkspaceSubaccountsUsersBaseStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.UserService) protected readonly userService: UserService) {}

  protected abstract redirectRoute: RouteLocationRaw

  protected abstract isCorrectWorkflowType(): boolean

  private getAccessRejectionReason(): SettingsWorkspaceAccessRejectionReason | null {
    if (this.userService.currentUser().subaccountType === UserRole.USER) {
      return 'role'
    }
    if (!this.isCorrectWorkflowType()) {
      return 'flow'
    }

    return null
  }

  public isAccessAllowed(): boolean {
    const accessRejectionReason = this.getAccessRejectionReason()
    return accessRejectionReason === null
  }

  public getRedirectRoute() {
    const accessRejectionReason = this.getAccessRejectionReason()

    if (accessRejectionReason === null) {
      throw new TmLogicError('"accessRejectionReason" is null')
    }

    if (accessRejectionReason === 'role') {
      return null
    }

    if (accessRejectionReason === 'flow') {
      return this.redirectRoute
    }

    throw new TmAbsurdError(accessRejectionReason)
  }
}

@injectable()
export class SettingsWorkspaceSubaccountsStrategy extends SettingsWorkspaceSubaccountsUsersBaseStrategy {
  constructor(
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
    @inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService,
  ) {
    super(userService)
  }

  protected redirectRoute = {
    name: UserSettingsRoutes.WorkspaceUsers,
  }

  protected isCorrectWorkflowType(): boolean {
    return this.teamWorkflowService.isSubaccountsWorkflow()
  }
}

@injectable()
export class SettingsWorkspaceUsersStrategy extends SettingsWorkspaceSubaccountsUsersBaseStrategy {
  constructor(
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
    @inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService,
  ) {
    super(userService)
  }

  protected redirectRoute = {
    name: UserSettingsRoutes.WorkspaceSubaccounts,
  }

  protected isCorrectWorkflowType(): boolean {
    return this.teamWorkflowService.isUsersWorkflow()
  }
}

@injectable()
export class NotesFeedStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.FeatureFlagsService) protected readonly featureFlagsService: FeatureFlagsService) {}

  public isAccessAllowed(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlags.NotesFeed)
  }
}

@injectable()
export class FoldersStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.FeatureFlagsService) protected readonly featureFlagsService: FeatureFlagsService) {}

  public isAccessAllowed(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlags.Folders)
  }
}

@injectable()
export class ContactListsStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.FeatureFlagsService) protected readonly featureFlagsService: FeatureFlagsService) {}

  public isAccessAllowed(): boolean {
    return !this.featureFlagsService.isFeatureEnabled(FeatureFlags.Folders)
  }

  public getRedirectRoute(route: RouteLocationNormalized): RouteLocationRaw {
    const listId = route.params[ListIdRouteParam]
    return listId
      ? { name: contactsListsFoldersContactListRouteName, params: { [ListIdRouteParam]: listId } }
      : { name: contactsListsFoldersRouteName }
  }
}

@injectable()
export class AutoRespondersStrategy implements AccessControlStrategy {
  constructor(@inject(SERVICE_TYPES.UserSettingsService) protected readonly userSettingsService: UserSettingsService) {}

  public getRedirectRoute(): TmRoutes {
    return {
      name: AutomationRoutes.flows,
    }
  }

  public isAccessAllowed(): boolean {
    const userSettings = this.userSettingsService.currentUserSettingsOrNull()

    if (!userSettings) {
      return true
    }

    return userSettings?.showAutoResponders
  }
}

@injectable()
export class ReportingEmailStrategy implements AccessControlStrategy {
  constructor(
    @inject(SERVICE_TYPES.FeatureFlagsService) protected readonly featureFlagsService: FeatureFlagsService,
    @inject(SERVICE_TYPES.PermissionsService) protected readonly permissionsService: PermissionsService,
    @inject(SERVICE_TYPES.EmailCampaignDomainsPreloaderService)
    protected readonly emailCampaignDomainsPreloaderService: EmailCampaignDomainsPreloaderService,
    @inject(SERVICE_TYPES.EmailCampaignDomainService)
    protected readonly emailCampaignDomainService: EmailCampaignDomainService,
  ) {}

  private canConnectDomains() {
    return this.permissionsService.isActivePermission('connectEmailDomains')
  }

  public async resolve() {
    await this.permissionsService.getPermissionsIfNotLoaded()
    if (this.canConnectDomains()) {
      return
    }
    await this.emailCampaignDomainsPreloaderService.preload()
  }

  public isAccessAllowed(): boolean {
    if (!this.featureFlagsService.isFeatureEnabled(FeatureFlags.ReportsEmail)) {
      return false
    }
    if (this.canConnectDomains()) {
      return true
    }
    const domains = this.emailCampaignDomainService.findAll()
    return domains.length > 0
  }
}
